import styled from 'styled-components';
import { HouseInfoProps, LandInfoProprs } from '../../data';
import ItemStateMap from './ItemStateMap';
import { useState } from 'react';
import BuildingInfo from './BuildingInfo';
import SummaryBuildingInfo from './SummaryBuildingInfo';
import LandInfo from './LandInfo';
import LandImg from './LandImg';
import LandUsePlan from './LandUsePlan';
import ItemAddress from './ItemAddress';
import ItemUseState from './ItemUseState';
import { useReportContext } from '../../../../context/DetailContext';

const category = {
  house: [
    '연면적',
    '건물층수',
    '건폐율',
    '용적률',
    '주용도',
    '건물구조',
    '노후년도',
  ],
  subHouseInfo: [
    '구분',
    '건물명',
    '주용도',
    '기타용도',
    '용적률산정연면적',
    '허가일',
    '주 건축물',
    '부속 건축물',
    '착공일',
    '총 호수/가구수/세대수',
    '외 필지수',
    '사용승인일',
    '위반내용',
  ],
  land: [
    '토지면적',
    '용도지역',
    '지목',
    '이용상황',
    '고저',
    '도로',
    '용적률',
    '건폐율',
  ],
};

interface itemStateProps {
  landInfo: LandInfoProprs;
  houseInfo: HouseInfoProps;
  landUsePlan: any;
  appraisalevaluation: string;
  locationInfo: string;
  subHouseInfo?: any;
  groupCategory: string;
}
const ItemState = ({
  landInfo,
  houseInfo,
  landUsePlan,
  appraisalevaluation,
  locationInfo,
  subHouseInfo,
  groupCategory,
}: itemStateProps) => {
  const [tab, setTab] = useState('land');

  const { type } = useReportContext();

  // landUsePlan이 빈 객체인지 확인하는 함수
  function hasNonNullValues(data: Record<string, any>): boolean {
    return Object.values(data).some((value) => {
      if (value === null || value === 'nodata') return false; // null 또는 '-'이면 계속 확인
      if (Array.isArray(value) && value.length === 0) return false; // 빈 배열이면 계속 확인
      if (
        typeof value === 'object' &&
        value !== null &&
        Object.keys(value).length === 0
      )
        return false; // 빈 객체이면 계속 확인
      if (typeof value === 'object' && value !== null) {
        // 재귀적으로 내부 확인 (nested object 처리)
        return hasNonNullValues(value);
      }
      return true; // 위 조건에 해당하지 않는 경우 true
    });
  }

  function removeDuplicatesFromLandUsePlan(
    data: Record<string, any>,
  ): Record<string, any> {
    const removeDuplicates = (array: any[]) => Array.from(new Set(array)); // 중복 제거

    const processObject = (obj: Record<string, any>) => {
      const result: Record<string, any> = {};
      for (const [key, value] of Object.entries(obj)) {
        if (Array.isArray(value)) {
          result[key] = removeDuplicates(value); // 배열이면 중복 제거
        } else if (typeof value === 'object' && value !== null) {
          result[key] = processObject(value); // 중첩 객체 처리
        } else {
          result[key] = value; // 나머지 값 그대로 복사
        }
      }
      return result;
    };

    return processObject(data);
  }

  const cleanedLandUsePlan = removeDuplicatesFromLandUsePlan(landUsePlan);

  const renderContent = (tab: string) => {
    if (tab === 'building') {
      return (
        <>
          {houseInfo?.approvaldate === 'nodata' ? (
            <NoDataBox>등록된 건물정보가 없습니다.</NoDataBox>
          ) : (
            <BuildingInfo houseInfo={houseInfo} />
          )}
          {subHouseInfo && subHouseInfo.length > 0 && (
            <SummaryBuildingInfo data={subHouseInfo}></SummaryBuildingInfo>
          )}
        </>
      );
    } else {
      return (
        <>
          {landInfo && landInfo?.address !== 'nodata' && (
            <>
              <LandInfo landInfo={landInfo} />
              <LandImg landInfo={landInfo} />
              {landUsePlan && hasNonNullValues(landUsePlan) && (
                <LandUsePlan landUsePlan={cleanedLandUsePlan} />
              )}
            </>
          )}
        </>
      );
    }
  };
  return (
    <Container id="물건현황">
      <div>물건현황</div>
      <ItemStateMap groupCategory={groupCategory} />
      {type === 'p' && (
        <>
          <ItemAddress data={locationInfo} />
          <ItemUseState appraisalevaluation={appraisalevaluation} />
        </>
      )}

      <TabButtonBox>
        <TabButton onClick={() => setTab('land')} isActive={tab === 'land'}>
          토지정보
        </TabButton>
        <TabButton
          onClick={() => setTab('building')}
          isActive={tab === 'building'}
        >
          건물정보
        </TabButton>
      </TabButtonBox>
      {renderContent(tab)}
    </Container>
  );
};

export default ItemState;

interface ButtonProps {
  isActive: boolean;
}

const TabButton = styled.div<ButtonProps>`
  width: 50%;
  padding: 8px;
  text-align: center;
  color: ${(props) => (props.isActive ? '#222222' : '#989BA2')};
  background-color: ${(props) => (props.isActive ? '#fff' : '')};
  box-shadow: ${(props) =>
    props.isActive ? '0px 0px 1px rgba(56, 58, 59, 0.08)' : 'none'};

  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-family: Spoqa Han Sans Neo;
  font-weight: 500;
  line-height: 20px;
  word-wrap: break-word;
`;

const TabButtonBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  padding: 4px;
  background-color: rgba(112, 115, 124, 0.05);
  border-radius: 8px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px 16px;
  background-color: #fff;
  color: #222222;
  font-size: 18px;
  font-family: Spoqa Han Sans Neo;
  font-weight: 700;
  line-height: 26.1px;
  word-wrap: break-word;
`;

const NoDataBox = styled.div`
  width: 100%;
  height: 180px;
  background-color: #eff2f5;
  border-radius: 8px;
  color: rgba(55, 56, 60, 0.61);
  font-size: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
`;
